import { Capacitor } from '@capacitor/core'

/**
 * A new tab opened DURING a click, pointed at its destination once that is
 * known.
 *
 * A browser lets a page open a tab only while the click that asked for it is
 * still "active", and an `await` spends that: Chrome allows about five
 * seconds, Firefox about one, and Safari considers it gone at the first
 * network round-trip. A WHMCS sign-in link is minted by the API on click (it
 * is single-use and short-lived, so it cannot be fetched in advance), so
 * `await mint(); window.open(url)` was blocked exactly when the API was slow —
 * which is how customers met the popup blocker on the resize top-up.
 *
 * So the tab is opened FIRST, synchronously, blank, and navigated after the
 * await. It is opened without `noopener` because we need its handle, and the
 * handle's `opener` is severed before it leaves our origin, which is what
 * `noopener` would have done.
 *
 * Call it synchronously from the click handler, before any `await`.
 */
export interface PendingTab {
  /** Send the tab to `url`. False when the customer already closed it. */
  navigate(url: string): boolean
  /** The destination could not be resolved: take the blank tab away again. */
  close(): void
}

export function openPendingTab(placeholder: string): PendingTab | null {
  if (typeof window === 'undefined') return null
  // The native shell has no popup blocker, and its WebView does not hand a
  // blank window back: the caller opens the URL directly once it has it.
  if (Capacitor.isNativePlatform()) return null

  let tab: Window | null = null
  try {
    tab = window.open('', '_blank')
  } catch {
    tab = null
  }
  if (!tab) return null

  // Until it navigates the tab is same-origin about:blank, so a line of text
  // stands in for the white page during the second the link takes.
  try {
    const doc = tab.document
    doc.title = placeholder
    doc.documentElement.lang = document.documentElement.lang
    const meta = doc.createElement('meta')
    meta.name = 'color-scheme'
    meta.content = 'light dark'
    doc.head?.appendChild(meta)
    const body = doc.body ?? doc.documentElement.appendChild(doc.createElement('body'))
    body.style.cssText =
      'margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font:15px system-ui,sans-serif;opacity:.7'
    body.textContent = placeholder
  } catch {
    // Cosmetic only.
  }

  const handle = tab
  return {
    navigate(url: string) {
      if (handle.closed) return false
      try {
        handle.opener = null
      } catch {
        // Some engines make `opener` read-only; the destination is ours anyway.
      }
      handle.location.replace(url)
      return true
    },
    close() {
      try {
        if (!handle.closed) handle.close()
      } catch {
        // Already gone.
      }
    },
  }
}
